// NEW LANDING PAGE: cloned from original App layout with minimal changes per requirements
// Changes: fixed transparent header (logo only), removed nav items & contact button,
// added subtle dot/page indicator & scroll snap, kept original section content & styling.

import * as React from 'react';
const { useState, useEffect, useCallback } = React;
import { ChevronsUp, Blocks, BrainCircuit } from 'lucide-react';
import {
  PixelatedCodeBackground,
  PipelineBackground,
  Pipeline3DBackground,
  SnakeBackground,
  TetrisBackground,
  PacmanBackground
} from './components/backgrounds';
import { getVersionFromUrl, getBodyStyleForVersion } from './utils/backgroundUtils';
import { Logo, CheckIcon, HeroSlider, DotNav } from './components/landing';

// Discord webhook URL for contact form submissions
const DISCORD_WEBHOOK_URL = 'https://discord.com/api/webhooks/1412837263330771014/r5j9A9nLWnzgHFgVi72MRFJgedOkWnG-Ski9s8TI8tZumCQOKvbU5mcZ1AkSysy6SKy7';

const NewLanding = () => {
  const [formData, setFormData] = useState({
    naam: '',
    email: '',
    bedrijfsnaam: '',
    bericht: ''
  });
  const [formStatus, setFormStatus] = useState('');
  const [statusIsError, setStatusIsError] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [version, setVersion] = useState('default');

  useEffect(() => { setVersion(getVersionFromUrl()); }, []);

  // Intersection observer to track active section for dots
  useEffect(() => {
    // Observe main sections for primary dots
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && entry.intersectionRatio > 0.55) {
          setActiveSection(entry.target.id);
        }
      });
    }, { threshold: [0.55] });
    ['home', 'specialisaties', 'contact'].forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('Bericht verzenden...');
    setStatusIsError(false);
    try {
      const embed = {
        embeds: [{
          title: 'Nieuwe contactformulier inzending',
          color: 0x3315e8,
          fields: [
            { name: 'Naam', value: formData.naam || 'Niet opgegeven', inline: true },
            { name: 'Email', value: formData.email || 'Niet opgegeven', inline: true },
            { name: 'Bedrijfsnaam', value: formData.bedrijfsnaam || 'Niet opgegeven', inline: true },
            { name: 'Bericht', value: formData.bericht || 'Geen bericht' }
          ],
          timestamp: new Date().toISOString()
        }]
      };

      const response = await fetch(DISCORD_WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(embed)
      });
      if (response.ok) {
        setFormStatus('Bedankt voor uw bericht!');
        setFormData({ naam: '', email: '', bedrijfsnaam: '', bericht: '' });
      } else {
        setFormStatus('Er is een fout opgetreden. Probeer het later opnieuw.');
        setStatusIsError(true);
      }
    } catch (err) {
      setFormStatus('Kan de server niet bereiken. Probeer het later opnieuw.');
      setStatusIsError(true);
    }
  };

  const scrollTo = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const bodyStyle = getBodyStyleForVersion(version);

  const renderBackground = () => {
    switch (version) {
      case '1': return <PixelatedCodeBackground />;
      case '2': return <PipelineBackground />;
      case '2.2': return <Pipeline3DBackground />;
      case '3': return <SnakeBackground />;
      case '4': return <TetrisBackground />;
      case '5': return <PacmanBackground />;
      default: return <PixelatedCodeBackground defaultOpacity={30} />;
    }
  };

  const hideHeaderDetails = activeSection === 'contact';

  return (
    <div style={bodyStyle} className="relative">
      {renderBackground()}
      {/* Header merged from original App with modified right side (phone & email icons) */}
      <header className="fixed top-0 left-0 w-full z-50 py-4 sm:py-5 px-4 sm:px-6 lg:px-8 bg-transparent transition-colors">
        <nav className="container mx-auto flex items-center relative">
          {/* Left: Logo */}
          <div className="flex-1 flex items-center">
            <button onClick={() => { scrollTo('home'); }} className="flex items-center space-x-2 text-[#0A2540]">
              <Logo />
              <span className="font-bold text-xl">Codepipeline</span>
            </button>
          </div>
          {/* Center navigation absolutely centered */}
          {!hideHeaderDetails && (
            <div className="hidden md:flex space-x-8 absolute left-1/2 -translate-x-1/2">
              <button onClick={() => { scrollTo('specialisaties'); }} className="text-sm font-medium text-[#0A2540] hover:text-[#3d68ff] transition-colors">Services</button>
              <button onClick={() => { scrollTo('contact'); }} className="text-sm font-medium text-[#0A2540] hover:text-[#3d68ff] transition-colors">Contact</button>
            </div>
          )}
          {/* Right: phone & email icons */}
          <div className="flex-1 flex justify-end items-center space-x-6">
            {!hideHeaderDetails && (
              <>
                <a href="tel:+31850602133" aria-label="Bel ons" className="flex items-center text-[#0A2540] hover:text-[#3d68ff] transition-colors group">
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 mr-2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
                  <span className="text-sm font-medium whitespace-nowrap hidden md:inline">+31 (0) 85 060 2133</span>
                </a>
                <a href="mailto:vraag@codepipeline.nl" aria-label="Email ons" className="flex items-center text-[#0A2540] hover:text-[#3d68ff] transition-colors group">
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 mr-2"><rect width="20" height="16" x="2" y="4" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></svg>
                  <span className="text-sm font-medium hidden md:inline">vraag@codepipeline.nl</span>
                </a>
              </>
            )}
          </div>
        </nav>
      </header>

      <DotNav active={activeSection} onNav={(id) => { scrollTo(id); }} />

      {/* Wrapper with scroll snap */}
      <div className="snap-y snap-mandatory scroll-smooth overflow-y-auto h-screen">
        {/* HERO */}
        <section id="home" className="snap-start min-h-screen flex flex-col relative z-10 pt-28">
          <main className="flex-grow flex flex-col justify-center container mx-auto px-4 sm:px-6 lg:px-8 relative">
            {/* Hero Slider */}
            <HeroSlider scrollTo={scrollTo} />
          </main>
        </section>

        {/* SPECIALISATIES (3 Kern-Oplossingen) */}
        <section id="specialisaties" className="snap-start flex flex-col justify-center py-16 sm:py-20 container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 min-h-screen">
          <div className="text-center mb-10 sm:mb-12 max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0A2540] mb-4">Our Core Business</h2>
            <p className="text-gray-600">Gerichte trajecten die strategie, techniek en automatisering samenbrengen voor schaalbare digitale groei.</p>
          </div>
          {/* Desktop / Tablet grid */}
          <div className="hidden md:grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Card 1 */}
            <div className="bg-white rounded-[21px] border border-gray-200 p-8 shadow-lg transition-transform hover:-translate-y-2 flex flex-col" data-card-index="0">
              <div className="flex-grow">
                <div className="bg-indigo-100 text-[#3d68ff] rounded-[21px] p-3 w-max mb-4">
                  <ChevronsUp className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold text-[#0A2540] mb-2">Razendsnelle Applicatie Ontwikkeling</h3>
                <p className="text-gray-600 mb-4">Maatwerk web- en mobiele applicaties die uw gebruikerservaring en operationele efficiëntie verbeteren.</p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center"><CheckIcon />Strategie & Prototyping</li>
                  <li className="flex items-center"><CheckIcon />AI Rapid Development</li>
                  <li className="flex items-center"><CheckIcon />Schaalbare Architectuur</li>
                  <li className="flex items-center"><CheckIcon />Onderhoud & Support</li>
                </ul>
                {/* RAD Tech Stack */}
                <div className="mt-6 pt-4 border-t border-gray-100">
                  <h4 className="text-xs font-semibold tracking-wider text-gray-500 uppercase mb-3">Including:</h4>
                  <ul className="flex flex-wrap gap-2">
                    {/* GitHub Copilot */}
                    <li className="flex items-center bg-gray-50 border border-gray-200 rounded-full px-2 py-1 text-xs font-medium text-gray-700">
                      <span>GitHub Copilot</span>
                    </li>
                    {/* React */}
                    <li className="flex items-center bg-gray-50 border border-gray-200 rounded-full px-2 py-1 text-xs font-medium text-gray-700">
                      <span>React</span>
                    </li>
                    {/* Vite */}
                    <li className="flex items-center bg-gray-50 border border-gray-200 rounded-full px-2 py-1 text-xs font-medium text-gray-700">
                      <span>Vite</span>
                    </li>
                    {/* Flutter */}
                    <li className="flex items-center bg-gray-50 border border-gray-200 rounded-full px-2 py-1 text-xs font-medium text-gray-700">
                      <span>Flutter</span>
                    </li>
                    {/* Node.js */}
                    <li className="flex items-center bg-gray-50 border border-gray-200 rounded-full px-2 py-1 text-xs font-medium text-gray-700">
                      <span>Node.js</span>
                    </li>
                    {/* MongoDB */}
                    <li className="flex items-center bg-gray-50 border border-gray-200 rounded-full px-2 py-1 text-xs font-medium text-gray-700">
                      <span>MongoDB</span>
                    </li>
                    {/* PostgreSQL */}
                    <li className="flex items-center bg-gray-50 border border-gray-200 rounded-full px-2 py-1 text-xs font-medium text-gray-700">
                      <span>PostgreSQL</span>
                    </li>
                  </ul>
                </div>
              </div>
              <button className="mt-6 block text-center border border-gray-300 text-[#0A2540] font-semibold rounded-[21px] px-6 py-2 bg-white hover:bg-gray-100 transition-colors">Meer Informatie</button>
            </div>
            {/* Card 2 */}
            <div className="bg-white rounded-[21px] border-2 border-[#3315e8] p-8 shadow-lg flex flex-col" data-card-index="1">
              <div className="flex-grow">
                <div className="bg-indigo-100 text-[#3d68ff] rounded-[21px] p-3 w-max mb-4">
                  <Blocks className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold text-[#0A2540] mb-2">Systeemintegratie & Modernisering</h3>
                <p className="text-gray-600 mb-4">Naadloze koppelingen tussen uw bestaande systemen en het upgraden van verouderde software voor betere prestaties en veiligheid.</p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center"><CheckIcon />API Ontwikkeling</li>
                  <li className="flex items-center"><CheckIcon />Nabouwen van Legacy Software</li>
                  <li className="flex items-center"><CheckIcon />Cloud Migratie</li>
                  <li className="flex items-center"><CheckIcon />Data Synchronisatie</li>
                </ul>
                {/* Integration & Modernization Tech Stack */}
                <div className="mt-6 pt-4 border-t border-gray-100">
                  <h4 className="text-xs font-semibold tracking-wider text-gray-500 uppercase mb-3">Including:</h4>
                  <ul className="flex flex-wrap gap-2">
                    {/* Python */}
                    <li className="flex items-center bg-gray-50 border border-gray-200 rounded-full px-2 py-1 text-xs font-medium text-gray-700">
                      <span>Python</span>
                    </li>
                    {/* Node.js */}
                    <li className="flex items-center bg-gray-50 border border-gray-200 rounded-full px-2 py-1 text-xs font-medium text-gray-700">
                      <span>Node.js</span>
                    </li>
                    {/* PostgreSQL */}
                    <li className="flex items-center bg-gray-50 border border-gray-200 rounded-full px-2 py-1 text-xs font-medium text-gray-700">
                      <span>PostgreSQL</span>
                    </li>
                    {/* MySQL */}
                    <li className="flex items-center bg-gray-50 border border-gray-200 rounded-full px-2 py-1 text-xs font-medium text-gray-700">
                      <span>MySQL</span>
                    </li>
                    {/* Docker */}
                    <li className="flex items-center bg-gray-50 border border-gray-200 rounded-full px-2 py-1 text-xs font-medium text-gray-700">
                      <span>Docker</span>
                    </li>
                    {/* Kubernetes */}
                    <li className="flex items-center bg-gray-50 border border-gray-200 rounded-full px-2 py-1 text-xs font-medium text-gray-700">
                      <span>Kubernetes</span>
                    </li>
                    {/* RabbitMQ */}
                    <li className="flex items-center bg-gray-50 border border-gray-200 rounded-full px-2 py-1 text-xs font-medium text-gray-700">
                      <span>RabbitMQ</span>
                    </li>
                  </ul>
                </div>
              </div>
              <button className="mt-6 block text-center border border-gray-300 text-[#0A2540] font-semibold rounded-[21px] px-6 py-2 bg-white hover:bg-gray-100 transition-colors">Meer Informatie</button>
            </div>
            {/* Card 3 */}
            <div className="bg-white rounded-[21px] border border-gray-200 p-8 shadow-lg transition-transform hover:-translate-y-2 flex flex-col" data-card-index="2">
              <div className="flex-grow">
                <div className="bg-indigo-100 text-[#3d68ff] rounded-[21px] p-3 w-max mb-4">
                  <BrainCircuit className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold text-[#0A2540] mb-2">Procesautomatisering</h3>
                <p className="text-gray-600 mb-4">Identificeren en automatiseren van repetitieve, handmatige taken om kosten te verlagen en de productiviteit te verhogen.</p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center"><CheckIcon />Workflow Analyse</li>
                  <li className="flex items-center"><CheckIcon />AI Agents</li>
                  <li className="flex items-center"><CheckIcon />Real-time Dashboards</li>
                  <li className="flex items-center"><CheckIcon />Planning Optimalisatie</li>
                </ul>
                {/* Process Automation Tech Stack */}
                <div className="mt-6 pt-4 border-t border-gray-100">
                  <h4 className="text-xs font-semibold tracking-wider text-gray-500 uppercase mb-3">Including:</h4>
                  <ul className="flex flex-wrap gap-2">
                    {/* Python */}
                    <li className="flex items-center bg-gray-50 border border-gray-200 rounded-full px-2 py-1 text-xs font-medium text-gray-700">
                      <span>Python</span>
                    </li>
                    {/* Apache Airflow */}
                    <li className="flex items-center bg-gray-50 border border-gray-200 rounded-full px-2 py-1 text-xs font-medium text-gray-700">
                      <span>Apache Airflow</span>
                    </li>
                    {/* Node.js */}
                    <li className="flex items-center bg-gray-50 border border-gray-200 rounded-full px-2 py-1 text-xs font-medium text-gray-700">
                      <span>Node.js</span>
                    </li>
                    {/* Cronjobs */}
                    <li className="flex items-center bg-gray-50 border border-gray-200 rounded-full px-2 py-1 text-xs font-medium text-gray-700">
                      <span>Cronjobs</span>
                    </li>
                  </ul>
                </div>
              </div>
              <button className="mt-6 block text-center border border-gray-300 text-[#0A2540] font-semibold rounded-[21px] px-6 py-2 bg-white hover:bg-gray-100 transition-colors">Meer Informatie</button>
            </div>
          </div>
          {/* Mobile vertical stack (instead of horizontal carousel) */}
          <div className="md:hidden space-y-6">
            {[
              {
                title: 'Razendsnelle Applicatie Ontwikkeling',
                desc: 'Maatwerk web- en mobiele applicaties die uw gebruikerservaring en operationele efficiëntie verbeteren.',
                icon: <ChevronsUp className="w-6 h-6" />,
                features: ['Strategie & Prototyping', 'AI Rapid Development', 'Schaalbare Architectuur', 'Onderhoud & Support'],
                service: 'applicatie_ontwikkeling',
                techStack: [
                  { name: 'GitHub Copilot', icon: (<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M7.328 1.47A.748.748 0 0 1 8 1.118a.748.748 0 0 1 .672.352c1.133 2.275 3.128 3.59 5.073 3.59.48 0 .869.39.869.869v.928c0 .48-.39.869-.869.869-1.945 0-3.94 1.315-5.073 3.59A.748.748 0 0 1 8 11.882a.748.748 0 0 1-.672-.352C6.195 9.255 4.2 7.94 2.255 7.94c-.48 0-.869-.39-.869-.869V6.143c0-.48.39-.869.869-.869 1.945 0 3.94-1.315 5.073-3.59Z" /></svg>) },
                  { name: 'React', icon: (<svg width="16" height="16" viewBox="0 0 256 256" fill="none"><circle cx="128" cy="128" r="12" fill="#61DAFB" /><g stroke="#61DAFB" strokeWidth="6" fill="none"><ellipse rx="60" ry="24" transform="matrix(.866 -.5 .5 .866 128 128)" /><ellipse rx="60" ry="24" transform="matrix(.866 .5 -.5 .866 128 128)" /><ellipse rx="60" ry="24" transform="matrix(1 0 0 1 128 128)" /></g></svg>) },
                  { name: 'Vite', icon: (<svg width="16" height="16" viewBox="0 0 256 257" fill="none"><defs><linearGradient id="viteGradient" x1="-.828%" x2="57.636%" y1="7.652%" y2="78.411%"><stop offset="0%" stopColor="#41D1FF" /><stop offset="100%" stopColor="#BD34FE" /></linearGradient></defs><path fill="url(#viteGradient)" d="m255.153 37.938-84.451 144.815c-1.246 2.14-4.264 2.14-5.51 0L80.741 37.938c-1.33-2.283.325-5.13 3.006-5.17L219.147 32.5c2.681-.04 4.336 2.887 3.006 5.17z" /><path fill="#FFCC02" d="m67.33 228.38 93.076-144.085c1.246-2.14 4.264-2.14 5.51 0L258.992 228.38c1.33 2.283-.325 5.13-3.006 5.17L72.336 233.55c-2.681.04-4.336-2.887-3.006-5.17z" /></svg>) },
                  { name: 'Flutter', icon: (<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M14.314 0L2.3 12 6 15.7 21.684.013h-7.357-.013zM21.684 8.013L8.314 21.384c-.05.05-.1.13-.15.18-.05.05-.1.13-.15.18-.05.05-.1.13-.15.18-.05.05-.1.13-.15.18-.05.05-.1.13-.15.18-.05.05-.1.13-.15.18-.05.05-.1.13-.15.18L14.314 24 21.685 16.64 16.6 11.54l5.084-3.527z" fill="#02569B" /></svg>) },
                  { name: 'Node.js', icon: (<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M12 1l11 6v10l-11 6L1 17V7l11-6z" fill="#8CC84B" /><path d="M7 10v4h2v-2h1v2h2v-4H7zm8 0v4h2v-2h1v2h2v-4h-5z" fill="white" /></svg>) },
                  { name: 'MongoDB', icon: (<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M17.193 9.555c-1.264-5.58-4.252-7.414-4.573-8.115-.28-.394-.53-.954-.735-1.44-.036.495-.055.685-.523 1.184-.723.566-4.438 3.682-4.74 10.02-.282 5.912 4.27 9.435 4.888 9.884l.07.05A73.49 73.49 0 0111.91 24h.481c.114-1.032.284-2.056.51-3.07.417-.296 5.124-3.972 4.292-11.375z" fill="#4DB33D" /></svg>) },
                  { name: 'PostgreSQL', icon: (<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M23.111 5.441c-.541-2.681-2.334-4.353-4.801-4.473C16.215.863 14.382 1.027 12 1.027S7.785.863 5.69.968C3.223 1.088 1.43 2.76.889 5.441c-.453 2.25-.568 5.269.245 9.159.813 3.891 2.334 7.127 4.801 7.127 1.892 0 3.447-.679 4.565-1.027 1.118.348 2.673 1.027 4.565 1.027 2.467 0 3.988-3.236 4.801-7.127.813-3.89.698-6.909.245-9.159zM12 2.027c2.225 0 4.122-.149 6.1-.014 1.839.106 3.145 1.405 3.589 3.569.369 1.798.4 4.22-.123 7.159-.634 3.567-1.918 6.232-3.566 6.232-1.425 0-2.711-.514-3.717-.811a10.17 10.17 0 00-2.283-.188c-.783 0-1.555.064-2.283.188-1.006.297-2.292.811-3.717.811-1.648 0-2.932-2.665-3.566-6.232-.523-2.939-.492-5.361-.123-7.159.444-2.164 1.75-3.463 3.589-3.569C7.878 1.878 9.775 2.027 12 2.027z" fill="#336791" /></svg>) }
                ]
              },
              {
                title: 'Systeemintegratie & Modernisering',
                desc: 'Naadloze koppelingen tussen uw bestaande systemen en het upgraden van verouderde software voor betere prestaties en veiligheid.',
                icon: <Blocks className="w-6 h-6" />,
                features: ['API Ontwikkeling', 'Nabouwen van Legacy Software', 'Cloud Migratie', 'Data Synchronisatie'],
                service: 'systeemintegratie_modernisering',
                featured: true,
                techStack: [
                  { name: 'Python', icon: (<svg width="16" height="16" viewBox="0 0 256 255"><path fill="#3776AB" d="M126 0c-58 0-54 25-54 25l.1 26h54v8H54S0 53 0 111s47 55 47 55h28v-39s-1-47 47-47h54s45 1 45-44c0-46-41-36-41-36s-10 0-54 0z" /><path fill="#FFE052" d="M130 255c58 0 54-25 54-25l-.1-26h-54v-8h72s54 6 54-52-47-55-47-55h-28v39s1 47-47 47h-54s-45-1-45 44c0 46 41 36 41 36s10 0 54 0z" /></svg>) },
                  { name: 'Node.js', icon: (<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M12 1l11 6v10l-11 6L1 17V7l11-6z" fill="#8CC84B" /><path d="M7 10v4h2v-2h1v2h2v-4H7zm8 0v4h2v-2h1v2h2v-4h-5z" fill="white" /></svg>) },
                  { name: 'PostgreSQL', icon: (<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M23.111 5.441c-.541-2.681-2.334-4.353-4.801-4.473C16.215.863 14.382 1.027 12 1.027S7.785.863 5.69.968C3.223 1.088 1.43 2.76.889 5.441c-.453 2.25-.568 5.269.245 9.159.813 3.891 2.334 7.127 4.801 7.127 1.892 0 3.447-.679 4.565-1.027 1.118.348 2.673 1.027 4.565 1.027 2.467 0 3.988-3.236 4.801-7.127.813-3.89.698-6.909.245-9.159zM12 2.027c2.225 0 4.122-.149 6.1-.014 1.839.106 3.145 1.405 3.589 3.569.369 1.798.4 4.22-.123 7.159-.634 3.567-1.918 6.232-3.566 6.232-1.425 0-2.711-.514-3.717-.811a10.17 10.17 0 00-2.283-.188c-.783 0-1.555.064-2.283.188-1.006.297-2.292.811-3.717.811-1.648 0-2.932-2.665-3.566-6.232-.523-2.939-.492-5.361-.123-7.159.444-2.164 1.75-3.463 3.589-3.569C7.878 1.878 9.775 2.027 12 2.027z" fill="#336791" /></svg>) },
                  { name: 'MySQL', icon: (<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M16.405 5.501c-.115 0-.193.014-.274.033v.013h.014c.054.104.146.18.214.274.054.107.1.214.154.32l.014-.015c.094-.066.14-.172.14-.333-.04-.047-.046-.094-.08-.14-.04-.067-.126-.1-.18-.153zM5.77 18.695h-.927a50.854 50.854 0 00-.27-4.41h-.008l-1.41 4.41H2.45l-1.4-4.41h-.01a72.892 72.892 0 00-.195 4.41H.117c.055-1.966.192-3.81.41-5.53h1.15l1.335 4.064h.008l1.347-4.064h1.095c.242 1.72.384 3.564.438 5.53zm4.017-4.08c-.378 2.045-.876 3.533-1.492 4.46-.482.716-1.01 1.073-1.583 1.073-.153 0-.34-.046-.566-.138v-.494c.11.017.24.026.386.026.268 0 .483-.075.647-.222.197-.18.295-.382.295-.605 0-.155-.077-.47-.23-.944L6.23 14.615h.91l.727 2.36c.164.536.233.91.205 1.123.4-1.064.678-2.227.835-3.483h.88zm12.325 4.08h-2.63v-5.53h.885v4.85h1.745v.68z" fill="#E97627" /><path d="M12.023 18.695h-.856c.024-1.932.057-3.755.098-5.53h.764c-.064 1.775-.006 3.598.006 5.53z" fill="#E97627" /></svg>) },
                  { name: 'Docker', icon: (<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M13.5 3H15v1.5h-1.5V3zm0 1.5H15V6h-1.5V4.5zm0 1.5H15v1.5h-1.5V6zm-1.5 0h1.5v1.5H12V6zm0-1.5h1.5V6H12V4.5zm0-1.5h1.5V4.5H12V3zm-1.5 3H12v1.5h-1.5V6zm0-1.5H12V6h-1.5V4.5zm-1.5 1.5h1.5v1.5H9V6zm0-1.5h1.5V6H9V4.5zm-1.5 1.5H9v1.5H7.5V6zm0-1.5H9V6H7.5V4.5zm-1.5 1.5h1.5v1.5H6V6zm6 3h1.5v1.5H12V9z" fill="#2496ED" /></svg>) },
                  { name: 'Kubernetes', icon: (<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M12 0L2.5 7v10L12 24l9.5-7V7L12 0z" fill="#326CE5" /><path d="M12 2.5l7.5 5.5v8L12 21.5 4.5 16V8L12 2.5z" fill="#FFF" /><circle cx="12" cy="12" r="3" fill="#326CE5" /></svg>) },
                  { name: 'RabbitMQ', icon: (<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M18.537 7.684c0 .365-.133.676-.398.93-.265.255-.589.383-.972.383-.383 0-.709-.128-.978-.383-.269-.255-.404-.565-.404-.93 0-.364.135-.673.404-.928.269-.255.595-.383.978-.383.383 0 .707.128.972.383.265.255.398.564.398.928zm-1.843-.522c-.194 0-.352.064-.474.193-.122.129-.183.298-.183.508 0 .21.061.379.183.508.122.129.28.193.474.193.194 0 .352-.064.474-.193.122-.129.183-.298.183-.508 0-.21-.061-.379-.183-.508-.122-.129-.28-.193-.474-.193z" fill="#FF6600" /><path d="M20.405 10.024h-1.6l-1.204-2.4h-1.6l1.6 3.2v3.2h1.6v-3.2l1.6-3.2h-1.396z" fill="#FF6600" /></svg>) }
                ]
              },
              {
                title: 'Procesautomatisering',
                desc: 'Identificeren en automatiseren van repetitieve, handmatige taken om kosten te verlagen en de productiviteit te verhogen.',
                icon: <BrainCircuit className="w-6 h-6" />,
                features: ['Workflow Analyse', 'AI Agents', 'Real-time Dashboards', 'Planning Optimalisatie'],
                service: 'procesautomatisering_gpt',
                techStack: [
                  { name: 'Python', icon: (<svg width="16" height="16" viewBox="0 0 256 255"><path fill="#3776AB" d="M126 0c-58 0-54 25-54 25l.1 26h54v8H54S0 53 0 111s47 55 47 55h28v-39s-1-47 47-47h54s45 1 45-44c0-46-41-36-41-36s-10 0-54 0z" /><path fill="#FFE052" d="M130 255c58 0 54-25 54-25l-.1-26h-54v-8h72s54 6 54-52-47-55-47-55h-28v39s1 47-47 47h-54s-45-1-45 44c0 46 41 36 41 36s10 0 54 0z" /></svg>) },
                  { name: 'Apache Airflow', icon: (<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M12 2l10 8-10 8L2 10l10-8z" fill="#017CEE" /><path d="M7 10l5-4 5 4-5 4-5-4z" fill="white" /></svg>) },
                  { name: 'Node.js', icon: (<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M12 1l11 6v10l-11 6L1 17V7l11-6z" fill="#8CC84B" /><path d="M7 10v4h2v-2h1v2h2v-4H7zm8 0v4h2v-2h1v2h2v-4h-5z" fill="white" /></svg>) },
                  { name: 'Cronjobs', icon: (<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="#4A5568" strokeWidth="2" fill="none" /><path d="M12 6v6l4 2" stroke="#4A5568" strokeWidth="2" strokeLinecap="round" /></svg>) }
                ]
              }
            ].map((card, idx) => (
              <div key={card.title} className={`bg-white rounded-[21px] p-6 shadow-lg flex flex-col ${card.featured ? 'border-2 border-[#3d68ff]' : 'border border-gray-200'}`}>
                <div className="flex-grow">
                  <div className="bg-indigo-100 text-[#3d68ff] rounded-[21px] p-3 w-max mb-4">
                    {card.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-[#0A2540] mb-2">{card.title}</h3>
                  <p className="text-gray-600 mb-4 text-sm">{card.desc}</p>
                  <ul className="space-y-2 text-gray-600 text-sm">
                    {card.features.map(feature => (
                      <li key={feature} className="flex items-center"><CheckIcon />{feature}</li>
                    ))}
                  </ul>
                  {/* Service-specific tech stack */}
                  {card.techStack && (
                    <div className="mt-6 pt-4 border-t border-gray-100">
                      <h4 className="text-xs font-semibold tracking-wider text-gray-500 uppercase mb-3">Including:</h4>
                      <ul className="flex flex-row flex-wrap gap-2" aria-label="Tech Stack">
                        {card.techStack.map(t => (
                          <li key={t.name} className="flex items-center bg-white border border-gray-200 rounded-full px-2 py-1 text-xs font-medium text-gray-700 shrink-0">
                            <span>{t.name}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <button className="mt-6 border border-gray-300 text-[#0A2540] font-semibold rounded-[21px] px-4 py-2 bg-white hover:bg-gray-100 transition-colors text-sm">Meer Informatie</button>
              </div>
            ))}
          </div>
        </section>

        {/* CONTACT + FOOTER (merged) */}
        <section id="contact" className="snap-start flex flex-col justify-center py-16 sm:py-20 container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 min-h-screen">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block bg-white border border-gray-200 rounded-full px-4 py-1.5 text-sm text-gray-600 mb-4">Laten we samenwerken</div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#0A2540]">Klaar om te <span className="text-[#3d68ff]">Beginnen?</span></h2>
              <p className="mt-4 text-lg text-gray-600">Laten we bespreken hoe onze aangepaste ontwikkeloplossingen uw bedrijfsprocessen kunnen stroomlijnen en uw groei kunnen versnellen.</p>
              <div className="mt-8 space-y-6">
                <div className="flex items-center">
                  <div className="bg-indigo-100 text-[#3d68ff] rounded-[21px] p-4 w-max">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></svg>
                  </div>
                  <div className="ml-4">
                    <h4 className="text-lg font-semibold text-[#0A2540]">Email</h4>
                    <a href="mailto:vraag@codepipeline.nl" className="text-gray-600 hover:text-[#3d68ff]">vraag@codepipeline.nl</a>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="bg-indigo-100 text-[#3d68ff] rounded-[21px] p-4 w-max">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
                  </div>
                  <div className="ml-4">
                    <h4 className="text-lg font-semibold text-[#0A2540]">Telefoon</h4>
                    <a href="tel:+31850602133" className="text-gray-600 hover:text-[#3d68ff]">+31 (0) 85 060 2133</a>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-[21px] border border-gray-200 p-8 shadow-lg">
              <h3 className="text-2xl font-semibold text-[#0A2540]">Neem Contact Op</h3>
              <p className="text-gray-600 mt-2 mb-6">Vertel ons over uw project en we nemen binnen 24 uur contact met u op.</p>
              <form onSubmit={handleSubmit} noValidate>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="naam" className="block text-sm font-medium text-gray-700">Naam *</label>
                    <input type="text" id="naam" value={formData.naam} onChange={handleInputChange} required placeholder="Uw volledige naam" className="mt-1 block w-full bg-gray-50 border-gray-200 rounded-[21px] p-4" />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email *</label>
                    <input type="email" id="email" value={formData.email} onChange={handleInputChange} required placeholder="uw.email@bedrijf.nl" className="mt-1 block w-full bg-gray-50 border-gray-200 rounded-[21px] p-4" />
                  </div>
                </div>
                <div className="mt-4">
                  <label htmlFor="bedrijfsnaam" className="block text-sm font-medium text-gray-700">Bedrijfsnaam</label>
                  <input type="text" id="bedrijfsnaam" value={formData.bedrijfsnaam} onChange={handleInputChange} placeholder="Uw bedrijfsnaam" className="mt-1 block w-full bg-gray-50 border-gray-200 rounded-[21px] p-4" />
                </div>
                <div className="mt-4">
                  <label htmlFor="bericht" className="block text-sm font-medium text-gray-700">Bericht *</label>
                  <textarea id="bericht" rows={4} value={formData.bericht} onChange={handleInputChange} required placeholder="Vertel ons over uw project, uitdagingen, of vragen..." className="mt-1 block w-full bg-gray-50 border-gray-200 rounded-[21px] p-4" />
                </div>
                <div className="mt-6">
                  <button type="submit" className="w-full bg-[#3315e8] text-white font-semibold rounded-[21px] px-8 py-3 hover:opacity-90 transition-opacity">Verstuur Bericht</button>
                </div>
                {formStatus && <p className={`mt-4 text-center ${statusIsError ? 'text-red-600' : 'text-green-600'}`}>{formStatus}</p>}
              </form>
            </div>
          </div>
          {/* Inline footer inside contact section */}
          <div className="mt-16 pt-10 border-t border-white/40">
            <div className="mx-auto max-w-5xl rounded-[28px] px-6 py-10 bg-[linear-gradient(to_right,rgba(255,255,255,0)_0%,rgba(255,255,255,0.92)_15%,rgba(255,255,255,0.95)_50%,rgba(255,255,255,0.92)_85%,rgba(255,255,255,0)_100%)] backdrop-blur-sm shadow-sm">
              <footer className="w-full text-center text-gray-500" role="contentinfo">
                <button onClick={() => { scrollTo('home'); }} className="flex justify-center items-center space-x-2 text-[#0A2540] mb-4 mx-auto">
                  <Logo width={24} height={24} idSuffix="FooterInline" />
                  <span className="font-bold text-lg">Codepipeline</span>
                </button>
                <p className="text-sm">&copy; 2024 Codepipeline. Professionele ontwikkeloplossingen voor moderne bedrijven.</p>
                {/* Phone & email moved to header per request */}
                <div className="mt-4 text-xs text-gray-400 leading-relaxed">
                  <p>KVK-nummer: 98041568 | Vestigingsnummer: 000063229439</p>
                  <p>Fascinatio Boulevard 708 8510, 2909VA Capelle aan den IJssel</p>
                </div>
              </footer>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default NewLanding;